import React, { useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../store/actions/loginActions";
import { useNavigate, useLocation } from "react-router";
import {
  Menu as MenuIcon,
  Person,
  ArrowDropDown,
  Close,
  ExpandMore,
  ExpandLess
} from "@mui/icons-material";
import ProfileModal from "./ProfileModal";
// import ServicesModal from "./ServicesModal";

function useWindowSize() {
  const [size, setSize] = useState([]);
  useEffect(() => {
    try {
      const handleResize = () =>
        setSize([window.innerHeight, window.innerWidth]);
      window.addEventListener("resize", handleResize);
      handleResize();
      return () => window.removeEventListener("resize", handleResize);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);
  return size;
}

export default function Header() {
  const [, width] = useWindowSize();
  const [showDrawer, setShowDrawer] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileStyle, setProfileStyle] = useState({});
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [servicesStyle, setServicesStyle] = useState({});
  const [showServicesInDrawer, setShowServicesInDrawer] = useState(false);

  const location = useLocation();
  const isHome = location.pathname === "/";
  // helper to check active route for specific nav items
  const isActive = (path) => location.pathname === path;

  const hoverTimeoutRef = useRef(null);
  const servicesHoverTimeoutRef = useRef(null);
  const userAvatarRef = useRef(null);
  const servicesButtonRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileAvatarRef = useRef(null);
  const mobileProfileModalRef = useRef(null);

  const navigate = useNavigate();
  const { loginInfo } = useSelector((state) => state.loginReducer);

  const goTo = (path, close = false) => {
    navigate(path);
    if (close) setShowDrawer(false);
    if (isProfileOpen) setIsProfileOpen(false);
    if (isServicesOpen) setIsServicesOpen(false);
  };

  // Services list for both desktop and mobile
  // const servicesList = [
  //   { label: "Home moves", path: "/services/home-moves" },
  //   { label: "Apartment moves", path: "/services/apartment-moves" },
  //   { label: "Condo moves", path: "/services/condo-moves" },
  //   { label: "Storage moves", path: "/services/storage-moves" },
  //   { label: "Office moves", path: "/services/office-moves" },
  //   { label: "Furniture delivery", path: "/services/furniture-delivery" },
  //   { label: "Appliance delivery", path: "/services/appliance-delivery" },
  //   { label: "Donation runs", path: "/services/donation-runs" },
  //   { label: "FB Marketplace delivery", path: "/services/fb-marketplace-delivery" },
  //   { label: "Kijiji delivery", path: "/services/kijiji-delivery" },
  //   { label: "Junk removal", path: "/services/junk-removal" },
  //   { label: "Labour only", path: "/services/labour-only" },
  // ];

  /* ================= DESKTOP PROFILE HOVER LOGIC ================= */
  const openProfile = () => {
    if (width <= 768) return; // Mobile pe hover nahi
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const el = userAvatarRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const modalWidth = 220;
    setProfileStyle({
      position: "fixed",
      top: `${rect.bottom + 8}px`,
      left: `${Math.max(8, rect.right - modalWidth)}px`,
    });
    setIsProfileOpen(true);
  };

  const closeProfile = () => {
    if (width <= 768) return; // Mobile pe hover nahi
    hoverTimeoutRef.current = setTimeout(() => {
      setIsProfileOpen(false);
    }, 150);
  };

  /* ================= MOBILE PROFILE CLICK LOGIC ================= */
  const openMobileProfile = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (width > 768) return; // Sirf mobile pe

    if (isProfileOpen) {
      // Agar already open hai to close karo
      setIsProfileOpen(false);
      return;
    }

    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const el = mobileAvatarRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const modalWidth = 220;

    setProfileStyle({
      position: "fixed",
      top: `${rect.bottom + 8}px`,
      left: `${Math.max(8, rect.right - modalWidth)}px`,
      zIndex: 9999,
    });
    setIsProfileOpen(true);
  };

  /* ================= SERVICES HOVER LOGIC ================= */
  const openServices = () => {
    if (width <= 768) return; // Mobile pe hover nahi
    if (servicesHoverTimeoutRef.current) clearTimeout(servicesHoverTimeoutRef.current);
    const el = servicesButtonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const modalWidth = 340;
    setServicesStyle({
      position: "fixed",
      top: `${rect.bottom + 8}px`,
      left: `${Math.max(8, rect.left)}px`,
    });
    setIsServicesOpen(true);
  };

  const closeServices = () => {
    if (width <= 768) return; // Mobile pe hover nahi
    servicesHoverTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 150);
  };

  /* ================= CLOSE MODAL ON OUTSIDE CLICK (MOBILE ONLY) ================= */
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Sirf mobile ke liye
      if (width <= 768 && isProfileOpen) {
        // Check karo k click modal ke bahar hai aur avatar pe bhi nahi hai
        if (
          mobileProfileModalRef.current &&
          !mobileProfileModalRef.current.contains(event.target) &&
          !mobileAvatarRef.current?.contains(event.target)
        ) {
          setIsProfileOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileOpen, width]);

  /* ================= MOBILE DRAWER CONTENT ================= */
  const drawerContent = (
    <div className="md:hidden fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => setShowDrawer(false)}
      />

      {/* Drawer Panel */}
      <div className="absolute right-0 top-0 h-full w-[280px] bg-white shadow-lg overflow-y-auto">
        {/* Drawer Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <div className="cursor-pointer" onClick={() => window.location.href = "https://moovez.ca"}>
            <img src="/images/moveesy.png" alt="logo" className="w-[160px]" />
          </div>
          <button onClick={() => setShowDrawer(false)}>
            <Close />
          </button>
        </div>

        {/* Drawer Menu Items */}
        <div className="p-4 space-y-4">
          {/* Profile Section - TOP */}
          {loginInfo && (
            <div className="pb-4 border-b">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <div
                  className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    openMobileProfile(e);
                    setShowDrawer(false);
                  }}
                >
                  {loginInfo.profileDisplayImageUrl ? (
                    <img
                      src={loginInfo.profileDisplayImageUrl}
                      className="w-full h-full rounded-full object-cover"
                      alt="profile"
                    />
                  ) : (
                    <Person className="text-white" />
                  )}
                </div>
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    goTo(`/userProfile/${loginInfo.email}`, true);
                  }}
                >
                  <div className="font-semibold text-[#211F59]">
                    {loginInfo.firstName} {loginInfo.lastName}
                  </div>
                  <div className="text-sm text-gray-500">View Profile</div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Section - BOTTOM */}
          <div className="space-y-4">



            {/* Services Logic and UI in Mobile Drawer is currently commented out */}
            {/* <div className="space-y-2">
              <div
                className="flex justify-between items-center font-semibold text-[#211F59] cursor-pointer"
                onClick={() => setShowServicesInDrawer(!showServicesInDrawer)}
              >
                <span>Services</span>
                {showServicesInDrawer ? <ExpandLess /> : <ExpandMore />}
              </div>

     
              {showServicesInDrawer && (
                <div className="pl-4 space-y-1">
                  {servicesList.map((service, index) => (
                    <div
                      key={index}
                      className="py-2 text-gray-700 hover:text-[#34318C] cursor-pointer"
                      onClick={() => goTo(service.path, true)}
                    >
                      {service.label}
                    </div>
                  ))}
                </div>
              )}
            </div> */}

            <div className="space-y-2">
              <div
                className="py-2 text-gray-700 hover:text-[#34318C] cursor-pointer"
                onClick={() => goTo("/business", true)}
              >
                Business
              </div>
              <div
                className="py-2 text-gray-700 hover:text-[#34318C] cursor-pointer"
                onClick={() => goTo("/BecomeAMoover", true)}
              >
                Become a Moover
              </div>
            </div>

            {/* Auth Buttons in Mobile - Only show if not logged in and not on booking flow */}
            {!loginInfo && !isHome && (
              <div className="pt-4 border-t space-y-3">
                <button
                  className="w-full flex items-center justify-center gap-2 h-[44px] rounded-full border border-[#34318C] text-[#34318C] bg-transparent"
                  onClick={() => goTo("/login", true)}
                >
                  <Person sx={{ fontSize: 20 }} />
                  Login
                </button>
                <button
                  className="w-full h-[44px] rounded-full bg-[#34318C] text-white font-semibold hover:bg-[#211F59] transition-colors"
                  onClick={() => goTo("/signup", true)}
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Book Now Button in Mobile */}
            {!isHome && (
              <button
                className="w-full h-[44px] rounded-full bg-[#34318C] text-white font-semibold hover:bg-[#211F59] transition-colors"
                onClick={() => goTo("/", true)}
              >
                Book Now
              </button>
            )}
          </div>



        </div>
      </div>
    </div>
  );

  return (
    <>
      <header
        className="w-full h-[60px] flex items-center justify-between relative z-30 bg-transparent"
        style={{
          paddingLeft: window.innerWidth >= 1300 ? "170px" : "16px",
          paddingRight: window.innerWidth >= 1300 ? "170px" : "16px",
        }}
      >
        {/* Logo - Responsive sizing */}
        <div className="cursor-pointer" onClick={() => window.location.href = "https://moovez.ca"}>
          <img
            src="/images/moveesy.png"
            alt="logo"
            className={width <= 900 ? "w-[130px] md:w-[140px]" : "w-[200px]"}
          />
        </div>

        <div className="flex items-center gap-4">
          {/* Desktop Nav - md screen se upar */}
          <nav className="hidden md:flex items-center gap-4 lg:gap-[35px] text-base lg:text-lg">
            {/* Services Button */}
            {/* <div
              ref={servicesButtonRef}
              className="relative flex items-center cursor-pointer text-[#211F59] hover:text-[#34318C] transition-colors"
              onMouseEnter={openServices}
              onMouseLeave={closeServices}
            >
              Services <ArrowDropDown />
            </div> */}

            {!isHome && (
              <div
                onClick={() => goTo("/")}
                className={`${isActive('/') ? 'text-[#55AB26]' : 'text-[#211F59] hover:text-[#55AB26]'} cursor-pointer transition-colors duration-300 whitespace-nowrap`}
              >
                Schedule pickup
              </div>
            )}

            {!isHome && (
              <div
                onClick={() => goTo("/business")}
                className="cursor-pointer text-[#211F59] hover:text-[#55AB26] transition-colors duration-300 whitespace-nowrap"
              >
                Business
              </div>
            )}

            {!isHome && (
              <div
                onClick={() => goTo("/BecomeAMoover")}
                className={`${isActive('/BecomeAMoover') ? 'text-[#55AB26]' : 'text-[#211F59] hover:text-[#55AB26]'} cursor-pointer transition-colors duration-300 whitespace-nowrap`}
              >
                Become a Moover
              </div>

            )}

            {!isHome && (

              <div
                onClick={() => goTo("/HowItWorks")}
                className={`${isActive('/HowItWorks') ? 'text-[#55AB26]' : 'text-[#211F59] hover:text-[#55AB26]'} cursor-pointer transition-colors duration-300 whitespace-nowrap`}
              >
                How it works
              </div>

            )}



            {/* Book Now Button */}
            {/* {!isHome && (
              <button
                className="h-[44px] w-[110px] rounded-full bg-[#34318C] text-white font-semibold hover:bg-[#211F59] transition-colors whitespace-nowrap"
                onClick={() => goTo("/")}
              >
                Book Now
              </button>
            )} */}
          </nav>

          {/* Right Side: Auth + Mobile Menu */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Desktop Auth - md screen se upar */}
            {loginInfo ? (
              <div className="hidden md:flex items-center gap-2 lg:gap-4">
                {/* Profile Name - lg screen se upar */}
                <div
                  className="
    flex items-center gap-3
    px-3 py-1.5
    border border-gray-300
    rounded-full
    cursor-pointer
    hover:border-[#55AB26]
    transition-colors
  "
                >
                  {width >= 1024 && (
                    <span
                      onMouseEnter={openProfile}
                      onMouseLeave={closeProfile}
                      className="text-[#211F59] text-sm font-medium whitespace-nowrap">
                      {loginInfo.firstName} {loginInfo.lastName}
                    </span>
                  )}

                  <div
                    ref={userAvatarRef}
                    onMouseEnter={openProfile}
                    onMouseLeave={closeProfile}
                    className="w-[36px] h-[36px] rounded-full bg-[#55AB26] flex items-center justify-center"
                  >
                    {loginInfo.profileDisplayImageUrl ? (
                      <img
                        src={loginInfo.profileDisplayImageUrl}
                        className="w-full h-full rounded-full object-cover"
                        alt="profile"
                      />
                    ) : (
                      <Person className="text-white text-[18px]" />
                    )}
                  </div>
                </div>


                {/* Profile Modal */}
                <div
                  ref={userMenuRef}
                  onMouseEnter={() => clearTimeout(hoverTimeoutRef.current)}
                  onMouseLeave={closeProfile}
                >
                  {isProfileOpen && width > 768 && (
                    <ProfileModal
                      loginInfo={loginInfo}
                      style={profileStyle}
                      onClose={() => setIsProfileOpen(false)}
                    />
                  )}
                </div>
              </div>
            ) : !isHome ? (
              <div className="hidden md:flex items-center gap-2 lg:gap-4">
                {/* LOGIN BUTTON */}
                <button
                  className="flex items-center gap-2 h-[44px] px-3 lg:px-4 transition-colors whitespace-nowrap"
                  onClick={() => goTo("/login")}
                >
                  <Person sx={{ fontSize: 20 }} />
                  Sign In
                </button>

                {/* SIGN UP BUTTON */}
                <button
                  className="h-[40px] w-[100px] lg:w-[110px] rounded-full bg-[#34318C] text-white font-semibold hover:bg-[#211F59] transition-colors whitespace-nowrap"
                  onClick={() => goTo("/signup")}
                >
                  Sign Up
                </button>
              </div>
            ) : null}

            {/* Mobile Sign In (sidebar/hamburger intentionally kept hidden) */}
            {!loginInfo && !isHome && (
              <button
                className="md:hidden flex items-center gap-1 text-[#211F59] font-medium"
                onClick={() => goTo("/login")}
              >
                <Person sx={{ fontSize: 20 }} />
                Sign In
              </button>
            )}

            {/* Mobile Logged-in Profile Avatar */}
            {loginInfo && width < 768 && (
              <div
                ref={mobileAvatarRef}
                className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center cursor-pointer hover:ring-2 ring-[#34318C] transition-shadow"
                onClick={openMobileProfile}
              >
                {loginInfo.profileDisplayImageUrl ? (
                  <img
                    src={loginInfo.profileDisplayImageUrl}
                    className="w-full h-full rounded-full object-cover"
                    alt="profile"
                  />
                ) : (
                  <Person className="text-white" />
                )}
              </div>
            )}

            {/* Mobile Menu Button + Mobile Profile Avatar */}
            <div className="flex items-center gap-3 hidden">
              {/* Mobile Profile Avatar (show on all screens below md) */}
              {loginInfo && width < 768 && (
                <div
                  // ref={mobileAvatarRef}
                  className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center cursor-pointer hover:ring-2 ring-[#34318C] transition-shadow"
                  onClick={openMobileProfile}
                >
                  {loginInfo.profileDisplayImageUrl ? (
                    <img
                      src={loginInfo.profileDisplayImageUrl}
                      className="w-full h-full rounded-full object-cover"
                      alt="profile"
                    />
                  ) : (
                    <Person className="text-white" />
                  )}
                </div>
              )}

              {/* Hamburger Menu */}
              <button
                className="md:hidden text-[#211F59]"
                onClick={() => setShowDrawer((s) => !s)}
              >
                {showDrawer ? <Close /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>

        {/* ✅ Services Modal */}
        {/* {isServicesOpen && width > 768 && (
          <div
            onMouseEnter={() => clearTimeout(servicesHoverTimeoutRef.current)}
            onMouseLeave={closeServices}
            style={servicesStyle}
            className="fixed z-[9999]"
          >
            <ServicesModal onClose={() => setIsServicesOpen(false)} />
          </div>
        )} */}

        {/* ✅ Mobile Profile Modal */}
        {isProfileOpen && width <= 768 && (
          <div
            ref={mobileProfileModalRef}
            style={profileStyle}
            className="fixed z-[9999]"
          >
            <ProfileModal
              loginInfo={loginInfo}
              style={{}}
              onClose={() => setIsProfileOpen(false)}
            />
          </div>
        )}
      </header>

      {/* Mobile Drawer */}
      {showDrawer && drawerContent}
    </>
  );
}